import type { ComparisonPeriod } from "@aco/core";

export type ComparisonMode = "period" | "wow" | "mom" | "none";

/**
 * Calculate the comparison period based on the selected date range and mode.
 * - period: shift back by same number of days (default)
 * - wow: same days, 7 days earlier
 * - mom: same day range, previous month
 */
export function getComparisonPeriod(
  start: string,
  end: string,
  mode: ComparisonMode = "period",
): ComparisonPeriod | null {
  if (mode === "none") return null;

  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");
  const daysInPeriod =
    Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  if (mode === "wow") {
    const compStart = new Date(startDate);
    compStart.setDate(compStart.getDate() - 7);
    const compEnd = new Date(endDate);
    compEnd.setDate(compEnd.getDate() - 7);
    return {
      start: compStart.toLocaleDateString("sv-SE"),
      end: compEnd.toLocaleDateString("sv-SE"),
    };
  }

  if (mode === "mom") {
    const previousMonth = (date: Date) => {
      const targetMonthStart = new Date(date.getFullYear(), date.getMonth() - 1, 1);
      const lastDay = new Date(
        targetMonthStart.getFullYear(),
        targetMonthStart.getMonth() + 1,
        0,
      ).getDate();
      return new Date(
        targetMonthStart.getFullYear(),
        targetMonthStart.getMonth(),
        Math.min(date.getDate(), lastDay),
      );
    };
    const compStart = previousMonth(startDate);
    const compEnd = previousMonth(endDate);
    return {
      start: compStart.toLocaleDateString("sv-SE"),
      end: compEnd.toLocaleDateString("sv-SE"),
    };
  }

  // Default: previous period (shift back by same number of days)
  const comparisonEnd = new Date(startDate);
  comparisonEnd.setDate(comparisonEnd.getDate() - 1);
  const comparisonStart = new Date(comparisonEnd);
  comparisonStart.setDate(comparisonStart.getDate() - (daysInPeriod - 1));

  return {
    start: comparisonStart.toLocaleDateString("sv-SE"),
    end: comparisonEnd.toLocaleDateString("sv-SE"),
  };
}
